import { useRef, useState, useEffect, useCallback } from "react";
import { Copy, Check, FileDown, Loader2, Info, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CVTemplate from "@/components/CVTemplate";
import ProspectorTemplate from "@/components/templates/ProspectorTemplate";
import VanguardTemplate from "@/components/templates/VanguardTemplate";
import SimpleTemplate from "@/components/templates/SimpleTemplate";
import MinimalistTemplate from "@/components/templates/MinimalistTemplate";
import { parseCV, type CVData } from "@/lib/cvParser";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { generateCVFilename } from "@/lib/filename";

interface CVOutputProps {
  data: CVData;
  onUpdate: (data: CVData) => void;
  loading: boolean;
  jobTitle?: string;
  company?: string;
  isFullScreen?: boolean;
  onBack: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}
const SkeletonLoader = () => (
  <div className="space-y-6 p-6">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="space-y-3">
        <div className="skeleton-line w-1/3" />
        <div className="skeleton-line w-full" />
        <div className="skeleton-line w-5/6" />
        <div className="skeleton-line w-4/6" />
      </div>
    ))}
  </div>
);

// ── Robust white-gap scanner ────────────────────────────────────────────────
// Scans UP from `targetY` for a large search range. 
// Finds the LARGEST contiguous block of white rows and cuts in its middle.
// This is much better for finding gaps between bullet points/jobs.
function findWhiteCutPoint(canvas: HTMLCanvasElement, targetY: number, maxSearchPx = 250): number {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return targetY;

  const width = canvas.width;
  let currentGapStart = -1;
  let bestGapY = targetY;
  let maxGapHeight = 0;

  for (let dy = 0; dy <= maxSearchPx; dy++) {
    const y = targetY - dy;
    if (y < 0) break;

    const row = ctx.getImageData(0, y, width, 1).data;
    let whitePixels = 0;
    for (let x = 0; x < width; x++) {
      const i = x * 4;
      // relaxed white threshold to handle minor anti-aliasing artifacts
      if (row[i] > 245 && row[i + 1] > 245 && row[i + 2] > 245) whitePixels++;
    }

    const isWhiteRow = (whitePixels / width) > 0.98;

    if (isWhiteRow) {
      if (currentGapStart === -1) currentGapStart = y;
    } else {
      if (currentGapStart !== -1) {
        const gapHeight = currentGapStart - y;
        if (gapHeight > maxGapHeight) {
          maxGapHeight = gapHeight;
          bestGapY = Math.floor(y + gapHeight / 2);
        }
        currentGapStart = -1;
      }
    }
  }

  // Final check if the loop ended while in a gap
  if (currentGapStart !== -1) {
    const gapHeight = currentGapStart - (targetY - maxSearchPx);
    if (gapHeight > maxGapHeight) {
      bestGapY = Math.floor((targetY - maxSearchPx) + gapHeight / 2);
    }
  }

  return bestGapY;
}

const CVOutput = ({ data, onUpdate, loading, jobTitle, company, isFullScreen, onBack, onRefresh, isRefreshing }: CVOutputProps) => {
  if (!data) return null;
  const templateRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.85);

  const updateScale = useCallback(() => {
    if (containerRef.current) {
      const padding = window.innerWidth < 768 ? 12 : (isFullScreen ? 48 : 96);
      const availableWidth = containerRef.current.offsetWidth - padding;
      const targetWidth = 794;
      
      let newScale = availableWidth / targetWidth;
      const isMobile = window.innerWidth < 768;
      
      // Apply multipliers and constraints
      if (isMobile) {
        // Force 0.45 scale for mobile to ensure fit and readability
        newScale = 0.45;
      } else if (isFullScreen) {
        newScale = Math.min(newScale, 1.1);
      } else {
        newScale = Math.min(newScale, 0.85);
      }
      
      // Minimum scale to keep it readable
      newScale = Math.max(newScale, 0.3);
      
      setScale(newScale);
    }
  }, [isFullScreen]);

  useEffect(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale]);

  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    () => localStorage.getItem("alvaru_selected_template") || "Classic"
  );
  const { user } = useAuth();

  const handleTemplateChange = (val: string) => {
    setSelectedTemplate(val);
    localStorage.setItem("alvaru_selected_template", val);
  };

  const handleCopy = async () => {
    // We could format the data back to raw text, but usually users want the optimised AI text
    // For now let's just copy the name and job title or some representation
    const rawText = `${data.name}\n${data.jobTitle}\n\nPROFILE\n${data.profile}\n\n...`;
    await navigator.clipboard.writeText(rawText);
    setCopied(true);
    toast.success("Copied processed text");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const { generateCVBlob } = await import("@/lib/pdfGenerator");
      const blob = await generateCVBlob(data, selectedTemplate);
      
      const nameParts = (data.name || "").trim().split(" ");
      let first = nameParts[0] || "";
      let last = nameParts.slice(1).join(" ") || "";

      if (first === first.toUpperCase()) first = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
      if (last === last.toUpperCase()) last = last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();
      
      const fileName = generateCVFilename(
        first, 
        last, 
        (jobTitle && !jobTitle.includes('.pdf') ? jobTitle : data.jobTitle) || "CV", 
        company || ""
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("PDF generation failed.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return <SkeletonLoader />;

  return (
    <div className="flex flex-col h-full bg-zinc-900/50">
      <div className="bg-zinc-950 px-3 py-2 sm:px-6 sm:py-4 border-b border-zinc-900 flex items-center justify-start sm:justify-between gap-2 sticky top-0 z-20 backdrop-blur-md">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="text-zinc-400 hover:text-white shrink-0"
        >
          <ChevronLeft className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Back to inputs</span>
        </Button>

        <div className="flex gap-1.5 sm:gap-3 items-center min-w-0">
          <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
            <SelectTrigger className="w-[100px] sm:w-[160px] h-8 sm:h-9 bg-zinc-900 border-zinc-800 text-zinc-300 text-xs sm:text-sm">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Classic">Classic</SelectItem>
              <SelectItem value="Prospector">Prospector</SelectItem>
              <SelectItem value="Vanguard">Vanguard</SelectItem>
              <SelectItem value="Simple">Simple</SelectItem>
              <SelectItem value="Minimalist">Minimalist</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" className="h-8 sm:h-9 bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800 px-2 sm:px-3 text-xs sm:text-sm" onClick={handleCopy}>
            {copied ? <Check className="w-3.5 h-3.5 sm:mr-2" /> : <Copy className="w-3.5 h-3.5 sm:mr-2" />}
            <span className="hidden xs:inline">{copied ? "Copied" : "Copy Raw"}</span>
          </Button>
          <Button
            size="sm"
            className="h-8 sm:h-9 bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 px-2 sm:px-4 text-xs sm:text-sm"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 sm:mr-2 animate-spin" /> : <FileDown className="w-3.5 h-3.5 sm:mr-2" />}
            <span className="hidden xs:inline">{pdfLoading ? "Wait..." : "PDF"}</span>
            <span className="xs:hidden">PDF</span>
          </Button>
        </div>
      </div>
      
      <div ref={containerRef} className="flex-1 overflow-y-auto overflow-x-auto p-4 sm:p-8 md:p-12 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y pinch-zoom' }}>
        <div style={{ display: "flex", justifyContent: "center", minHeight: "100%" }}>
          <div
            className="cv-preview-wrapper"
            style={{
              transformOrigin: "top center",
              transform: `scale(${scale})`,
              width: "794px",
              marginBottom: window.innerWidth < 768 ? "40px" : "100px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              flexShrink: 0,
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            {selectedTemplate === "Classic" ? (
              <CVTemplate 
                ref={templateRef} 
                data={data} 
                onUpdate={onUpdate}
              />
            ) : selectedTemplate === "Prospector" ? (
              <ProspectorTemplate 
                ref={templateRef} 
                data={data} 
                onUpdate={onUpdate}
              />
            ) : selectedTemplate === "Vanguard" ? (
              <VanguardTemplate 
                ref={templateRef} 
                data={data} 
                onUpdate={onUpdate}
              />
            ) : selectedTemplate === "Minimalist" ? (
              <MinimalistTemplate 
                ref={templateRef} 
                data={data} 
                onUpdate={onUpdate}
              />
            ) : (
              <SimpleTemplate 
                ref={templateRef} 
                data={data} 
                onUpdate={onUpdate}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-zinc-950/80 backdrop-blur-md border-t border-zinc-900 p-3 flex items-center justify-center gap-2">
        <Info className="w-3.5 h-3.5 text-primary" />
        <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest">
          A4 High-Fidelity Preview • Interactive Editor
        </p>
      </div>
    </div>
  );
};

export default CVOutput;
